import { getClientCookie } from '../cookiesUtils';
import experimentsConfig from './config';
import { EXPERIMENTS_SESSION_ID } from './const';
import { ExperimentAnalyticsEvent, ExperimentClient, ScreenResolution } from './types';
import { eventList, getScreenResolutionString } from './utils/client-utils';

interface Ga4 {
  getVariable(variable: string): Promise<string>;
  assignEvent(ga4Key: string | undefined, eventName: string, event?: ExperimentAnalyticsEvent): void;
}

interface Hotjar {
  trackEvent(eventName: string): void;
  setUserAttributes(ga4Key: string | undefined, attributes: Record<string, string>): void;
}

/**
 * Aggregates client-side logic for experiments
 */
// eslint-disable-next-line import/prefer-default-export
export function analytics(experimentClient: ExperimentClient | null) {
  let gaDispatcher: Ga4 | null = null;
  let hotjarDispatcher: Hotjar | null = null;

  const { experiments, metadata } = experimentClient ?? {
    experiments: [],
    metadata: {},
  };

  function loadGaInstance(gaInstance: Ga4 | null) {
    if (!experimentsConfig.analytics.enabled) {
      return;
    }

    gaDispatcher = gaInstance;
  }

  function loadHotjarInstance(hotjarInstance: Hotjar | null) {
    if (!experimentsConfig.analytics.enabled) {
      return;
    }

    hotjarDispatcher = hotjarInstance;
  }

  function dispatchEvents() {
    if (experiments.length === 0) {
      return;
    }

    const eventsToDispatch = eventList(experiments, experimentsConfig.analytics.enabled);
    if (eventsToDispatch.length === 0) {
      return;
    }

    eventsToDispatch.forEach((event) => {
      gaDispatcher?.assignEvent(experimentsConfig.analytics.ga4Key, 'experiment_assigned', event);

      hotjarDispatcher?.trackEvent(`experiment_${event.experience}_${event.variationName}_displayed`);
      hotjarDispatcher?.setUserAttributes(experimentsConfig.analytics.ga4Key, {
        [event.experience]: event.variationName,
      });
    });
  }

  async function handshake(internalApiUrl: string, originUrl: string, screenResolution: ScreenResolution) {
    if (!metadata || !metadata.ids || metadata.ids.length === 0) {
      return;
    }

    const gaClientId = (await gaDispatcher?.getVariable('client_id')) ?? null;
    const gaSessionId = (await gaDispatcher?.getVariable('session_id')) ?? null;

    const sessionId = getClientCookie(EXPERIMENTS_SESSION_ID);

    await fetch(internalApiUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ids: metadata.ids,
        gaClientId,
        gaSessionId,
        sessionId,
        url: originUrl,
        screenResolution: getScreenResolutionString(screenResolution),
      }),
    });
  }

  return {
    loadGaInstance,
    loadHotjarInstance,
    dispatchEvents,
    handshake,
  };
}
